import React, { useEffect, useState, useRef } from 'react';
import { Menu, Rocket, X, ArrowRight, Zap, Brush, CheckCircle, Lightbulb, TrendingUp, Cpu, Users, Code, Repeat, Server, DollarSign, Eye, Instagram, Mail, Phone, MapPin, Facebook, Linkedin, MessageCircle } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

const services = [
  {
    id: 'dev',
    title: 'Website Design and Development',
    description: 'At Vyxion Labs, we craft custom websites that combine stunning design with seamless functionality. Our expert team ensures your site engages visitors and drives real business results.',
    features: ['Custom Web Designs', 'Responsive Design', 'Security'],
    image: '/web_design.jpg',
    gradient: 'from-cyan-500 to-blue-600',
    cursorColor: 'rgba(147, 51, 234, 0.3)',
    hoverCursorColor: 'rgba(126, 34, 206, 0.5)',
    link: '/web-development',
    icon: Code,
  },
  {
    id: 'revamp',
    title: 'Website Revamp',
    description: 'Are you looking to give your website a fresh new look? Discover the benefits of our professional Website Revamp service. From enhanced functionality to improved user experience, we help you maximize your conversions.',
    features: ['Modern UI/UX Design', 'Performance Boost', 'Mobile Optimization', 'Brand Refresh'],
    image: '/web_revamp.jpg',
    gradient: 'from-cyan-500 to-blue-600',
    cursorColor: 'rgba(34, 211, 238, 0.3)',
    hoverCursorColor: 'rgba(37, 99, 235, 0.5)',
    link: '/website-revamp',
    icon: Brush,
  },
  {
    id: 'hosting',
    title: 'Server Hosting & Maintenance',
    description: 'Reliable, secure hosting solutions with 24/7 monitoring and expert maintenance to keep your site running flawlessly.',
    features: ['24/7 Server Monitoring', 'Security Updates', 'Content Revision', 'Technical Support'],
    image: '/server_hosting.jpg',
    gradient: 'from-cyan-500 to-blue-600',
    cursorColor: 'rgba(168, 85, 247, 0.3)',
    hoverCursorColor: 'rgba(34, 211, 238, 0.5)',
    link: '/hosting-maintenance',
    icon: Server,
  },
];


const timelineSteps = [
    {
        id: 1,
        title: "Consultation & Planning",
        icon: Users,
        description: "Deep dive into your goals and brand. We finalize the scope and site architecture.",
        color: "text-cyan-500",
        bg: "bg-cyan-500",
        numberPositionClass: 'lg:bottom-[-30px] lg:left-1/2 lg:-translate-x-1/2', 
    },
    {
        id: 2,
        title: "UI/UX Design",
        icon: Lightbulb,
        description: "We craft visual mockups, focusing on modern UI/UX principles and usability.",
        color: "text-cyan-500",
        bg: "bg-cyan-500",
        numberPositionClass: 'lg:bottom-[-80px] lg:left-1/2 lg:-translate-x-1/2 ',
    },
    {
        id: 3,
        title: "Client Feedback",
        icon: CheckCircle,
        description: "You review the design prototype, providing essential feedback before development begins.",
        color: "text-cyan-500",
        bg: "bg-cyan-500",
        numberPositionClass: 'lg:bottom-[-58px] lg:left-1/2 lg:-translate-x-1/2', 
    },
    {
        id: 4,
        title: "Web Development",
        icon: Code,
        description: "Our engineers build the site and bring the approved design to life.",
        color: "text-cyan-500",
        bg: "bg-cyan-500",
        numberPositionClass: 'lg:bottom-[-79px] lg:left-1/2 lg:-translate-x-1/2', 
    },
    {
        id: 5,
        title: "Tweaking & Revision",
        icon: Repeat,
        description: "Final quality assurance, rigorous testing, and minor adjustments based on final user feedback.",
        color: "text-cyan-500",
        bg: "bg-cyan-500",
        numberPositionClass: 'lg:bottom-[-28px] lg:left-1/2 lg:-translate-x-1/2', 
    },
    {
        id: 6,
        title: "Maintenance",
        icon: Server,
        description: "Secure deployment, reliable hosting setup, and ongoing technical support and maintenance.",
        color: "text-cyan-500",
        bg: "bg-cyan-500",
        numberPositionClass: 'lg:bottom-[-56px] lg:left-1/2 lg:-translate-x-1/2', 
    },
];



const HorizontalTimelineStep = ({ step, isVisible, delay, isLast }) => {
  const StepIcon = step.icon;
  const animationClass = isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-90";

  return (
      <div
          id={`timeline-step-${step.id}`}
          data-observe
          className={`flex flex-row lg:flex-col items-start lg:items-center text-left lg:text-center transition-all duration-700 w-full relative mb-12 lg:mb-0 ${animationClass}`}
          style={{ transitionDelay: delay }}
      >
          <div className="relative flex flex-col items-center flex-shrink-0 z-10">
              {!isLast && (
                  <div className={`lg:hidden absolute top-14 left-1/2 -translate-x-1/2 w-px bg-neutral-200 -z-10 transition-all duration-1000 ${isVisible ? 'h-24' : 'h-0'}`} />
              )}
              
              <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full border-2 border-cyan-600 bg-white p-2 relative shadow-lg group hover:scale-110 transition-transform duration-300`}>
                  <StepIcon className="w-6 h-6 md:w-8 md:h-8 text-cyan-600" />
                  
                  <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full ${step.bg} border-2 border-white text-white text-[10px] font-bold flex items-center justify-center`}>
                      {step.id}
                  </div>
              </div>
          </div>

          <div className="ml-6 lg:ml-0 lg:mt-6 px-2">
              <h4 className="text-lg font-bold text-neutral-900 mb-1">{step.title}</h4>
              <p className="text-sm text-neutral-600 leading-relaxed max-w-xs">{step.description}</p>
          </div>
      </div>
  );
};

const WhyWorkWithUsSection = ({ visible }) => {
  const isVisible = visible['why-work-us'];

  const steps = [
    "Understand Your Goals: We take the time to understand your business objectives and what success looks like for you.",
    "Industry Research: We analyze your industry, competitors, and target audience to inform a tailored strategy.",
    "Custom Strategy: We develop a digital strategy aligned with your business goals to maximize impact.",
    "Design & Development: We create websites or campaigns that reflect your brand identity and engage your audience.",
    "Launch & Optimize: We ensure the website or campaign performs at its best through testing and optimization.",
    "Maintenance: We ensure tht your website is kept up to date with changes and hosted.",
    "Ongoing Support: We ensure your website is protected against attacks and provide regular backups and security updates."
  ];

  return (
    <section id="why-work-us" data-observe className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
      <div className="max-w-5xl mx-auto">
        
        {/* Header - Fade Down */}
        <div className={`text-center mb-12 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-900">
            Why work with us?
          </h2>
          <p className="text-neutral-600 text-lg mt-4">
            At Vyxion Labs, we believe in a collaborative approach with our clients. Our process typically involves the following steps:
          </p>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

          {/* Left Column: Images coming from LEFT */}
          <div className={`relative h-[650px] flex items-start justify-center lg:justify-start transition-all duration-1000 delay-200 transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-32'}`}>
            
            {/* Image 1 */}
            <img
              src="/whyus1.jpg"
              alt="Why Us 1"
              className={`absolute left-0 w-64 rounded-lg shadow-lg z-10 border-2 border-white transition-all duration-1000 delay-500 ease-out
                ${isVisible ? 'top-0 rotate-[-6deg]' : 'top-32 rotate-0 scale-90 opacity-0'}
              `}
            />
            {/* Image 2 */}
            <img
              src="/whyus2.jpg"
              alt="Why Us 2"
              className={`absolute left-24 w-64 rounded-lg shadow-lg z-20 border-2 border-white transition-all duration-1000 delay-700 ease-out
                ${isVisible ? 'top-32 rotate-[5deg]' : 'top-32 rotate-0 scale-90 opacity-0'}
              `}
            />
            {/* Image 3 */}
            <img
              src="/whyus3.jpg"
              alt="Why Us 3"
              className={`absolute left-4 w-64 rounded-lg shadow-lg z-30 border-2 border-white transition-all duration-1000 delay-900 ease-out
                ${isVisible ? 'top-64 rotate-[-4deg]' : 'top-32 rotate-0 scale-90 opacity-0'}
              `}
            />
            {/* Image 4 */}
            <img
              src="/whyus4.jpg"
              alt="Why Us 4"
              className={`absolute left-28 w-64 rounded-lg shadow-lg z-40 border-2 border-white transition-all duration-1000 delay-1000 ease-out
                 ${isVisible ? 'top-96 rotate-[3deg]' : 'top-32 rotate-0 scale-90 opacity-0'}
              `}
            />
          </div>

          {/* Right Column: Text List coming from RIGHT */}
          <div className={`flex flex-col pt-4 transition-all duration-1000 delay-300 transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-32'}`}>
            <ul className="space-y-6">
              {steps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-purple-600 font-bold mt-1">→</span>
                  <p className="text-neutral-700 leading-relaxed">{step}</p>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>
    </section>
  );
};



const CTABannerSection = ({ visible }) => {
  return (
    <section className="relative py-20 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div
          id="cta-banner"
          data-observe
          className={`flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-0 p-10 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-purple-500/10 shadow-xl border border-slate-200 transition-all duration-1000 ease-out ${
            visible['cta-banner'] ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-20"
          }`}
        >
          {/* Left: CTA Text + Button */}
          <div className={`lg:w-1/2 text-center lg:text-left space-y-4 transition-all duration-1000 delay-300 ${visible['cta-banner'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <p className="text-lg md:text-xl font-medium text-neutral-700">
              Got an idea you're excited about?
            </p>
            <h2 className="text-3xl md:text-6xl font-bold leading-tight text-neutral-900">
              Let's bring it to life. <br />
              <span className="font-serif italic text-cyan-600">All it takes is a conversation!</span>
            </h2>

            <a
              href="/consult"
              className="inline-flex items-center gap-2 px-8 py-3 bg-cyan-500 text-white font-bold rounded-xl shadow-lg hover:bg-cyan-600 transition-all hover:scale-105 mt-4"
            >
              Start the Conversation <ArrowRight className="w-5 h-5" />
            </a>
          </div>

          {/* Right: Contact Info */}
          <div className={`lg:w-1/2 text-center lg:text-right space-y-3 font-semibold transition-all duration-1000 delay-500 ${visible['cta-banner'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="text-2xl md:text-4xl font-bold text-neutral-900">
              For enquiries, email us:
            </div>
            <div className="text-lg md:text-2xl text-neutral-700 break-all">
              vyxionlabs.info@gmail.com
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};



const FooterSection = () => {
  return (
      <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
          <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                  {/* Column 1: Brand & Contact */}
                  <div className="col-span-2 md:col-span-2 space-y-6">
                      <a href="/" className="flex items-center gap-3">
                          
                          <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                          <div>
                              <div className="text-xl font-bold text-white">Vyxion Labs</div>
                              <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                          </div>
                      </a>
                      <p className="text-sm text-slate-400 max-w-xs">
                          We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                      </p>
                      
                      <div className="flex space-x-4 pt-2">
                      <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                      <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                          
                      </div>
                  </div>

                  {/* Column 2: Quick Links */}
                  <div className="space-y-4">
                      <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                      <ul className="space-y-3">
                          <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                          <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                          <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                          <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                          <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                          
                      </ul>
                  </div>

                  {/* Column 3: Services */}
                  <div className="space-y-4">
                      <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                      <ul className="space-y-3">
                          {services.map((s, index) => (
                              <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                          ))}
                      </ul>
                  </div>
                  
                  {/* Column 4: Get In Touch */}
                  <div className="space-y-4 col-span-2 md:col-span-1">
                      <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                      <ul className="space-y-3">
                          <li className="flex items-center gap-3">
                              <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                              <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                          </li>
                      </ul>
                  </div>
              </div>
              
              {/* --- LEGAL LINKS --- */}
              <div className="flex justify-center md:justify-start space-x-6 mt-8">
                 
                  <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
              </div>
              {/* ------------------- */}

              {/* Copyright */}
              <div className="mt-4 text-center text-sm text-slate-500">
                  &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
              </div>
          </div>
      </footer>
  );
};

export default function LandingPageRevamp() {
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [visible, setVisible] = useState({});
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [hoveredService, setHoveredService] = useState(null);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [cardRect, setCardRect] = useState(null); 
  const [cursorScale, setCursorScale] = useState(1); 
  
  const servicesDropdownRef = useRef(null); 

  const handleServicesClick = (e) => {
    e.stopPropagation(); 
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [servicesDropdownRef]);

  const handleMenuClick = () => {
    setIsContactPopupOpen(!isContactPopupOpen);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });

      if (cardRect && hoveredService !== null) {
        const x = e.clientX;
        const y = e.clientY;
        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        const distX = Math.abs(x - centerX);
        const distY = Math.abs(y - centerY);
        const normDistX = distX / (cardRect.width / 2);
        const normDistY = distY / (cardRect.height / 2);
        const maxNormDist = Math.max(normDistX, normDistY);
        const maxScale = 1.5; 
        const minScale = 1;  
        const proximity = 1 - Math.min(1, maxNormDist);
        const newScale = minScale + (maxScale - minScale) * proximity;
        setCursorScale(newScale);

      } else if (cursorScale !== 1) {
          setCursorScale(1); 
      }
    };
    
    if (window.matchMedia("(hover: hover)").matches) {
        window.addEventListener('mousemove', handleMouseMove);
    }
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cardRect, hoveredService, cursorScale]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible((v) => ({ ...v, [e.target.id]: true }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-observe]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white text-neutral-900 min-h-screen antialiased relative overflow-x-hidden font-inherit">
      <style>{`
        @keyframes float { 
          0%, 100% { transform: translateY(0px) rotate(0deg); } 
          50% { transform: translateY(-20px) rotate(5deg); } 
        }
        @keyframes slideInDown {
            0% { transform: translateY(-100%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInUp {
            0% { transform: translateY(50px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInRight {
            0% { transform: translateX(100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes zoomInSlow {
            0% { transform: scale(1.1); }
            100% { transform: scale(1); }
        }
        @keyframes zoomOutSlow {
            0% { transform: scale(1); }
            100% { transform: scale(1.1); }
        }
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            z-index: 9999; 
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open {
            opacity: 1;
            visibility: visible;
        }
        .modal-content {
            position: fixed;
            top: 0;
            right: 0;
            width: 100%;
            max-width: 400px;
            height: 100%;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
            transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto;
            padding: 2rem 1.5rem;
        }
        .modal-overlay.open .modal-content {
            transform: translateX(0);
        }
        .float-animation { animation: float 6s ease-in-out infinite; }
        .custom-cursor {
          pointer-events: none;
          position: fixed;
          width: 60px; 
          height: 60px; 
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: all 0.15s ease-out; 
          z-index: 9999;
          mix-blend-mode: multiply;
        }
        .animate-slideInDown { animation: slideInDown 0.8s ease-out forwards; }
        .animate-slideInUp { animation: slideInUp 1s ease-out forwards; }
        .animate-slideInLeft { animation: slideInLeft 1s ease-out forwards; }
        .animate-slideInRight { animation: slideInRight 1s ease-out forwards; }
        .animate-zoomOut { animation: zoomOutSlow 20s linear infinite alternate; }
        
        /* Delays */
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-300 { animation-delay: 300ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-500 { animation-delay: 500ms; }
      `}</style>

      {hoveredService !== null && (
        <div
          className="custom-cursor hidden lg:block"
          style={{
            left: `${cursorPos.x}px`,
            top: `${cursorPos.y}px`,
            backgroundColor: isHoveringButton 
              ? services[hoveredService].hoverCursorColor 
              : services[hoveredService].cursorColor,
            transform: `translate(-50%, -50%) scale(${cursorScale * (isHoveringButton ? 1.3 : 1)})`,
            width: '60px',
            height: '60px',
          }}
        />
      )}

      {/* Header with Animation */}
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0 animate-slideInDown">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            
            {/* Logo - Left */}
            <a href="/" className="flex items-center gap-3 z-50 animate-slideInLeft delay-200 opacity-0 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox to prevent overlap */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8 animate-slideInDown delay-300 opacity-0 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-900 font-semibold transition-colors whitespace-nowrap">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium cursor-pointer whitespace-nowrap" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                    <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden transition-all duration-300 z-[110]">
                    <div className="flex flex-col p-2">
                        <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                            Website Design & Development
                        </a>
                        <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                            Website Revamp
                        </a>
                        <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                            Server Hosting & Maintenance
                        </a>
                    </div>
                </div>
                
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium whitespace-nowrap">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium whitespace-nowrap">About Us</a>
                <a href="/ourworks" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium  whitespace-nowrap">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium  whitespace-nowrap">FAQ</a>
            </nav>

            {/* Right Side: CTA, Socials, Menu */}
            <div className="flex items-center gap-3 md:gap-4 animate-slideInRight delay-200 opacity-0 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                
                {/* Desktop: Free Consult Button */}
                <a
                  href="/consult"
                  className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm hover:shadow-lg"
                >
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">
                    Free Consult
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </a>

                {/* Social Icons (Extreme Right on Desktop, Left of Menu on Mobile) */}
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors" aria-label="TikTok">
                        <FaTiktok className="w-4 h-4 md:w-5 md:h-5" />
                    </a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors" aria-label="Instagram">
                        <Instagram className="w-4 h-4 md:w-5 md:h-5" />
                    </a>
                </div>

                {/* Mobile: Hamburger Menu (Extreme Right on Mobile) */}
                <button
                    className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2"
                    onClick={handleMenuClick}
                >
                    <span className="relative z-10 flex items-center gap-1">
                        <Menu className="w-5 h-5" />
                        <span className="hidden sm:inline">Menu</span>
                    </span>
                </button>
            </div>

          </div>
        </div>
      </header>
      
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
            <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100 opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards'}}>
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 transition-colors animate-slideInLeft delay-200 opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards'}} onClick={handleMenuClick}>Pricing</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 transition-colors animate-slideInLeft delay-200 opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards'}} onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 transition-colors animate-slideInLeft delay-300 opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards'}} onClick={handleMenuClick}>About Us</a>
                <a href="/ourworks" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 transition-colors animate-slideInLeft delay-400 opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards'}} onClick={handleMenuClick}>Our Works</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>

            <h3 className="text-2xl font-bold text-neutral-900 mb-4 animate-slideInUp">Get in Touch</h3>
            <div className="space-y-4 text-left mb-10 animate-slideInUp delay-100">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50 hover:border-cyan-500 transition-colors">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
            
           

        </div>
      </div>

      <main>
        <section className="relative min-h-[90vh] md:min-h-screen flex items-center justify-center px-4 md:px-6 overflow-hidden">
          {/* Background Image - Cinematic Slow Zoom */}
          <div className="absolute inset-0 z-0 overflow-hidden">
            <img
              src="/mountainhand 2.png"
              alt="Mountain background"
              className="w-full h-full object-cover animate-zoomOut origin-center"
              loading="eager"
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>

          {/* Content - Staggered Slide Up */}
          <div className="relative z-10 text-center max-w-4xl mx-auto space-y-8">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight text-white animate-slideInUp opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards', animationDelay: '0.2s'}}>
             Redefine Your Online Presence with Vyxion Labs
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 font-medium animate-slideInUp opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards', animationDelay: '0.5s'}}>
            Ready to take the next step for your brand?
            </p>

            <div className="animate-slideInUp opacity-0 fill-mode-forwards" style={{animationFillMode: 'forwards', animationDelay: '0.8s'}}>
                <a
                href="/consult"
                className="inline-flex items-center gap-3 px-10 py-5 bg-white text-black font-semibold rounded-md hover:bg-neutral-200 transition-all text-lg shadow-xl "
                >
                Book Free Consult
                <ArrowRight className="w-6 h-6" />
                </a>
            </div>
          </div>
        </section>

        
        <section className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 overflow-hidden">
  <div className="grid lg:grid-cols-2 gap-16 items-start">
    
    {/* Left Column: Comes from LEFT */}
    <div id="vision-left" data-observe className={`space-y-8 transition-all duration-1000 ease-out ${visible['vision-left'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`}>
      <div className="space-y-4">
        
        <h3 className="text-4xl md:text-5xl font-extrabold text-neutral-900 leading-tight">
          Bridging the gap between Vision and Digital Reality.
        </h3>
      </div>

      <div className="space-y-6 text-base md:text-lg text-neutral-600 leading-relaxed">
        <p>
          Vyxion Labs was founded on a simple realization: most businesses don't just need a website—they need a growth engine. In an ever-evolving digital landscape, we build secure, and high-converting digital websites that truly empower businesses.
        </p>
        <p>
          Our methodology fuses technical precision with creative strategy. Every line of code and every pixel we craft is purpose-driven, designed to enhance user experience, drive engagement, and solidify long-term authority in your niche. We’re not just developers—we’re your strategic digital partners.
        </p>
       
      </div>

      <div className="flex flex-wrap gap-4 pt-20">
        <a 
          href="/about" 
          className="flex items-center gap-2 px-8 py-4 bg-neutral-950 text-white font-bold rounded-full hover:bg-neutral-1050 transition-all shadow-xl shadow-neutral-200 hover:scale-105 "
        >
          Learn More About Us
          <ArrowRight className="w-5 h-5 transition-transform" />
        </a>
      </div>
    </div>

   {/* Right Column: Comes from RIGHT */}
<div id="vision-right" data-observe className={`flex flex-col items-center gap-8 transition-all duration-1000 ease-out ${visible['vision-right'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
  {/* Team Title */}
  <div className="text-center">
    <h3 className="text-3xl font-bold text-neutral-900">Meet Our Team</h3>
    <p className="text-neutral-600 mt-2">The visionaries driving Vyxion Labs forward.</p>
  </div>

  {/* Team Photos with Messages */}
  <div className="flex flex-col gap-12">
    {/* Ram Photo + Message */}
    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 group hover:scale-[1.02] transition-transform duration-500">
      <div className="relative w-48 h-48 md:w-64 md:h-64 rounded-2xl overflow-hidden border-8 border-white shadow-2xl">
        <img src="/ramphoto.jpg" alt="Ram" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Co-Founder</p>
          <p className="text-base font-semibold">Ram</p>
        </div>
      </div>
      <div className="bg-neutral-100 p-4 rounded-xl max-w-xs text-neutral-700 shadow-md">
        <p>"We believe in building digital experiences that don’t just look good but drive real results."</p>
      </div>
    </div>

    {/* Saef Photo + Message */}
    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 group hover:scale-[1.02] transition-transform duration-500">
      <div className="relative w-48 h-48 md:w-64 md:h-64 rounded-2xl overflow-hidden border-8 border-white shadow-2xl">
        <img src="/saefphoto.jpg" alt="Saef" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Co-Founder</p>
          <p className="text-base font-semibold">Saef</p>
        </div>
      </div>
      <div className="bg-neutral-100 p-4 rounded-xl max-w-xs text-neutral-700 shadow-md">
        <p>"Our mission is to turn visions into actionable, impactful digital realities."</p>
      </div>
    </div>
  </div>
</div>


  </div>
</section>




<section className="relative pt-0 mt-20 pb-20 px-4 md:px-6">
  <div className="max-w-7xl mx-auto">
    
    <div 
      id="services-intro" 
      data-observe 
      className={`${visible["services-intro"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"} text-center mb-16 md:mb-24 transition-all duration-1000`}
    >
      <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 text-neutral-900 tracking-tight">Our Services</h2>
      <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl mx-auto">
        We combine strategy, design, and technology to create digital ecosystems that work seamlessly together.
      </p>
    </div>

    {/* 3-Column Grid - Staggered Scale Up */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
      {services.map((s, idx) => {
        const Icon = s.icon;
        // Calculate delay for staggered effect
        const delay = idx * 200; 

        return (
          <div
            key={s.id}
            id={"service-" + s.id}
            data-observe
            style={{ transitionDelay: `${delay}ms` }}
            className={`${visible["service-" + s.id] ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-20 scale-95"} 
              transition-all duration-700 ease-out flex flex-col p-8 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-2xl hover:shadow-neutral-200 group hover:-translate-y-2`}
            onMouseEnter={(e) => { 
              setHoveredService(idx); 
              setCardRect(e.currentTarget.getBoundingClientRect()); 
            }}
            onMouseLeave={() => { 
              setHoveredService(null); 
              setIsHoveringButton(false); 
              setCardRect(null); 
              setCursorScale(1); 
            }}
          >
            {/* Icon Container */}
            <div className="mb-8 relative">
              <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${s.gradient} flex items-center justify-center text-white shadow-lg group-hover:rotate-6 transition-transform duration-300`}>
                {Icon ? <Icon className="w-7 h-7" /> : <div className="w-7 h-7 bg-white/20 rounded-full" />}
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-grow text-left">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">{s.title}</h3>
              <p className="text-neutral-600 leading-relaxed text-sm md:text-base mb-6">
                {s.description}
              </p>
              
              <ul className="space-y-3 mb-8">
                {s.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${s.gradient}`} />
                    <span className="text-neutral-700 text-sm font-medium">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Button */}
            <div className="mt-auto">
              <a 
                href={s.link} 
                className={`inline-flex items-center justify-center px-6 py-3 border border-neutral-200 text-neutral-900 font-bold rounded-md hover:bg-neutral-950 hover:text-white hover:border-neutral-950 transition-all duration-300 gap-2 w-full group/btn`}
                onMouseEnter={() => setIsHoveringButton(true)} 
                onMouseLeave={() => setIsHoveringButton(false)}
              >
                Find Out More 
                <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
              </a>
            </div>
          </div>
        );
      })}
    </div>
  </div>
</section>


        <WhyWorkWithUsSection visible={visible} />
        <section className="relative py-16 md:py-20 px-4 md:px-6 border-t border-neutral-200">
          <div className="max-w-7xl mx-auto">
            <div id="timeline-intro" data-observe className={`${visible["timeline-intro"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"} text-center mb-16 transition-all duration-1000`}>
              <h2 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-6 text-neutral-900">The Project Journey</h2>
              <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-3xl mx-auto mb-12">
                From initial consultation to flawless deployment, our structured 6-step process provides reliability.
              </p>
            </div>
            
            <div className="relative pt-4 pb-10">
                {/* Line drawing animation */}
                <div className={`absolute w-full h-px bg-neutral-300 top-auto lg:bottom-5 hidden lg:block transition-all duration-[2000ms] ease-linear origin-left ${visible["timeline-step-1"] ? 'scale-x-100' : 'scale-x-0'}`} />

                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-start gap-0 lg:gap-0">
                    {timelineSteps.map((step, index) => (
                        <HorizontalTimelineStep 
                            key={step.id}
                            step={step}
                            isVisible={visible[`timeline-step-${step.id}`]}
                            delay={`${index * 250}ms`} 
                            isLast={index === timelineSteps.length - 1}
                        />
                    ))}
                </div>
            </div>
          </div>
        </section>
        
        
        <CTABannerSection visible={visible} />
        <FooterSection />
      </main>
    </div>
  );
}